/* Общий вид экранов до входа в салон: вход, второй фактор, выбор салона. */

/*
 * ЦЕНТРИРОВАНИЕ ЧЕРЕЗ `margin: auto`, А НЕ ЧЕРЕЗ `align-items: center`.
 *
 * Разница видна только тогда, когда карточка ВЫШЕ экрана, — и тогда она стоит дорого:
 * выравнивание по центру обрезает верх карточки, и верхняя часть становится недостижимой
 * даже прокруткой. На выборе салона у человека может быть восемь салонов, у экрана
 * второго фактора — десять резервных кодов; на ноутбуке 768 px это ровно тот случай.
 * Автоматические поля центрируют так же, но переполнение оставляют прокручиваемым.
 *
 * `dvh` вместо `vh`: адресная строка мобильного браузера съедает высоту и возвращает её
 * при прокрутке, и на `vh` низ карточки уезжает под строку браузера.
 */
.page {
  display: flex;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: var(--space-6) var(--space-4);
  background: var(--bg-workspace);
}

.card {
  width: 100%;
  max-width: 400px;
  margin: auto;
  padding: var(--space-6);
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-pop);
}

.wide {
  max-width: 520px;
}

.brand {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--text-brand);
}

.title {
  margin: var(--space-4) 0 var(--space-1);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
}

.subtitle {
  margin: 0 0 var(--space-5);
  font-size: var(--font-size-md);
  color: var(--text-secondary);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-bottom: var(--space-4);
}

.label {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
}

.input {
  padding: 8px 11px;
  font-size: var(--font-size-md);
  color: var(--text-primary);
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: 7px;
}

.input:focus {
  border-color: var(--accent);
  box-shadow: var(--field-focus-ring);
  outline: none;
}

.hint {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.submit {
  width: 100%;
  padding: 10px 14px;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--bg-card);
  cursor: pointer;
  background: var(--accent);
  border: 0;
  border-radius: var(--radius-md);
}

.submit:hover:not(:disabled) {
  background: var(--accent-hover);
}

.submit:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

/*
 * Ошибка входа — ОДНА на форму, а не под каждым полем. Сервер намеренно отвечает
 * одинаково на «нет такого адреса» и «не тот пароль»: иначе форма входа бесплатно
 * отвечает на вопрос «работает ли Иванов в этом салоне».
 */
.error {
  padding: 10px 12px;
  margin-bottom: var(--space-4);
  font-size: var(--font-size-md);
  color: var(--danger-hover);
  background: var(--status-no-show-bg);
  border: 1px solid var(--danger);
  border-radius: var(--radius-md);
}

.errorTitle {
  font-weight: var(--font-weight-semibold);
}

/* ─────────── выбор салона ─────────── */

.tenantList {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: var(--space-4) 0 0;
  padding: 0;
  list-style: none;
}

.tenantButton {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
}

.tenantButton:hover:not(:disabled) {
  background: var(--bg-workspace);
}

.tenantButton:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.tenantName {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
}

.tenantRole {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.tenantBadge {
  margin-left: auto;
  padding: 2px 8px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--status-pending-fg);
  white-space: nowrap;
  background: var(--status-pending-bg);
  border-radius: var(--radius-pill);
}

.footNote {
  margin-top: var(--space-5);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.linkButton {
  padding: 0;
  font-size: inherit;
  color: var(--text-brand);
  text-decoration: underline;
  cursor: pointer;
  background: none;
  border: 0;
}

/* Строка-приглашение выделена рамкой (1-Э1.4): её ждут, и она не должна теряться в списке. */
.tenantInvite {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  background: var(--bg-card);
  border: 1.5px solid var(--status-pending-fg);
  border-radius: var(--radius-md);
}

.tenantHead {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.tenantNote {
  font-size: var(--font-size-sm);
  line-height: 1.45;
  color: var(--text-secondary);
}

.tenantNoteWarn {
  color: var(--status-pending-fg);
}

/* Строка, в которую нельзя войти, остаётся в списке и объясняет почему — но не нажимается. */
.tenantBlocked {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  opacity: 0.7;
}

.tenantConsents {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--grid-line-minor);
  font-size: 12.5px;
}

.tenantBadgeSlot {
  margin-left: auto;
  white-space: nowrap;
}

/*
 * ЗАВЕДЕНИЕ ВТОРОГО ФАКТОРА НА ВХОДЕ (Э1.2-б).
 *
 * Ключ и резервные коды переписывают с экрана руками, поэтому моноширинный шрифт
 * и разрядка: «l» рядом с «1» в пропорциональном шрифте не различить, а ошибка здесь
 * стоит человеку входа в кабинет.
 */
.secret {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-2);
  margin: 0;
  font-family: var(--font-mono, ui-monospace, Menlo, monospace);
  letter-spacing: 0.08em;
}

.secret > span {
  padding: 2px 6px;
  background: var(--bg-sunken);
  border-radius: var(--radius-xs);
}

.codes {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: var(--space-1) var(--space-2);
  padding: 10px 12px;
  margin: 0;
  font-family: var(--font-mono, ui-monospace, Menlo, monospace);
  letter-spacing: 0.06em;
  list-style: none;
  background: var(--bg-sunken);
  border-radius: var(--radius-md);
}
